<template>
  <v-container fluid class="ma-4">
    <!-- Filters toolbar -->
    <v-row justify="start" align="center">
      <!-- Status filter — hidden when the list IS a status drill-down -->
      <v-col
        v-if="!preApplicant && headerKey !== 'Status'"
        cols="12"
        md="2"
        class="py-0 my-0"
      >
        <v-autocomplete
          v-model="form.application_status"
          :items="statuses"
          outlined
          dense
          hide-details="auto"
          label="Status"
          clearable
        ></v-autocomplete>
      </v-col>

      <!-- Mobile (pre-applicant only) -->
      <v-col v-if="preApplicant" cols="12" md="2" class="py-0 my-0">
        <v-text-field
          v-model="form.mobile_no"
          outlined
          dense
          hide-details
          :rules="[rules.mobile]"
          maxlength="10"
          label="Mobile No"
          clearable
        ></v-text-field>
      </v-col>

      <!-- Program filter — hidden when already filtered by programme -->
      <v-col
        v-if="headerKey !== 'Programme' || preApplicant"
        cols="12"
        md="2"
        class="py-0 my-0"
      >
        <v-autocomplete
          v-model="form.program_id"
          :items="programs"
          :item-text="
            (item) =>
              `${item.degree.short_name}${
                item.specialization ? ` - ${item.specialization}` : ''
              }`
          "
          item-value="id"
          outlined
          dense
          hide-details="auto"
          label="Programme"
          clearable
        ></v-autocomplete>
      </v-col>

      <!-- Partner filter — hidden when already filtered by partner -->
      <v-col
        v-if="headerKey !== 'Partner' || preApplicant"
        cols="12"
        md="2"
        class="py-0 my-0"
      >
        <v-autocomplete
          v-model="form.partner_id"
          :items="partners"
          item-text="name"
          item-value="id"
          outlined
          dense
          hide-details="auto"
          label="Partner"
          clearable
        ></v-autocomplete>
      </v-col>

      <v-col v-if="!preApplicant" cols="12" md="2" class="py-0 my-0">
        <v-autocomplete
          v-model="form.verified_user_id"
          :items="verifierList"
          :item-text="(item) => `${item.first_name} ${item.last_name}`"
          item-value="id"
          outlined
          dense
          hide-details="auto"
          label="Verifier"
          clearable
        ></v-autocomplete>
      </v-col>

      <v-col v-if="!preApplicant" cols="12" md="2" class="py-0 my-0">
        <v-autocomplete
          v-model="form.reviewed_user_id"
          :items="reviewerList"
          :item-text="(item) => `${item.first_name} ${item.last_name}`"
          item-value="id"
          outlined
          dense
          hide-details="auto"
          label="Reviewer"
          clearable
        ></v-autocomplete>
      </v-col>

      <v-col v-if="!preApplicant" cols="12" md="2" class="py-0 my-0">
        <v-text-field
          v-model="form.application_id"
          outlined
          dense
          hide-details="auto"
          :rules="[rules.applicationNumber]"
          maxlength="15"
          label="Application ID"
          clearable
        ></v-text-field>
      </v-col>

      <v-col
        v-if="!preApplicant && currentUser.role === 'Partner'"
        cols="12"
        md="2"
        class="py-0 my-0"
      >
        <v-text-field
          v-model="form.employee_id"
          outlined
          dense
          hide-details="auto"
          label="Employee ID"
          clearable
        ></v-text-field>
      </v-col>

      <v-col cols="12" md="4">
        <v-btn class="accent white--text" :loading="loading" @click="search">
          Search
        </v-btn>
        <v-btn
          class="accent white--text ml-3"
          @click="
            $router.push({
              name: 'Operational Dashboard',
            })
          "
        >
          Back to Dashboard
        </v-btn>
      </v-col>
    </v-row>

    <!-- Applicant list table -->
    <v-row v-if="!preApplicant" class="mt-2">
      <v-col cols="12">
        <v-sheet outlined>
          <v-data-table
            :headers="dynamicHeaders"
            :items="reportData"
            fixed-header
          >
            <template v-slot:no-data>
              <div class="text-center">No data available</div>
            </template>
            <template v-slot:[`item.action`]="{ item }">
              <v-icon small class="mr-2" color="accent" @click="goToEdit(item)"
                >mdi-pencil</v-icon
              >
            </template>
            <template v-slot:[`item.program_id`]="{ item }">
              {{ getProgramName(item.program_id) }}
            </template>
            <template v-slot:[`item.partner_approval_required`]="{ value }">
              {{ value ? "Yes" : "No" }}
            </template>
            <template v-slot:[`item.partner_id`]="{ item }">
              {{ getPartnerName(item.partner_id) }}
            </template>
            <template v-slot:[`item.reviewed_user_id`]="{ item }">
              {{
                getUserName(
                  item?.reviewed_user_id ? item.reviewed_user_id : "-",
                )
              }}
            </template>
            <template v-slot:[`item.verified_user_id`]="{ item }">
              {{
                getUserName(
                  item?.verified_user_id ? item.verified_user_id : "-",
                )
              }}
            </template>
            <template v-slot:[`item.application_id`]="{ item }">
              {{ item.application_id }}
              <v-tooltip v-if="item.verifier_remarks" top>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                    small
                    :color="item.verifier_rejected === 'Y' ? 'red' : 'primary'"
                    class="ml-1"
                    v-bind="attrs"
                    v-on="on"
                    @click="
                      (rejectedReason = item.verifier_remarks),
                        (rejectDialog = true),
                        (currentApplicantId = item.application_id),
                        (isVerifier = true)
                    "
                  >
                    mdi-eye
                  </v-icon>
                </template>
                <span> Verifier Remarks</span>
              </v-tooltip>
            </template>
            <template v-slot:[`item.rejection_reason`]="{ item }">
              <v-chip
                small
                @click="
                  (rejectedReason = item.rejection_reason),
                    (rejectDialog = true),
                    (currentApplicantId = item.application_id),
                    (isVerifier = false)
                "
              >
                View
              </v-chip>
            </template>
            <template v-slot:[`item.pending_reason`]="{ item }">
              <div>
                <v-tooltip v-if="item.pendingHistory" top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon
                      class="mr-2"
                      color="accent"
                      v-bind="attrs"
                      v-on="on"
                      @click="getPendingHistory(item)"
                    >
                      mdi-dots-horizontal
                    </v-icon>
                  </template>
                  <span>There is no pending history</span>
                </v-tooltip>
                <v-icon
                  v-else
                  class="mr-2"
                  color="accent"
                  @click="getPendingHistory(item)"
                >
                  mdi-dots-horizontal
                </v-icon>
              </div>
            </template>
            <template v-slot:[`item.deb_validated`]="{ value }">
              {{ value ? "Yes" : "No" }}
            </template>
          </v-data-table>
        </v-sheet>
      </v-col>
    </v-row>

    <!-- Pre-applicant table -->
    <v-row v-if="preApplicant" class="mt-2">
      <v-col cols="12">
        <v-sheet outlined>
          <v-data-table
            :headers="preApplicantHeaders"
            :items="preApplicantData"
            class="pre-applicant"
            fixed-header
          >
            <template v-slot:no-data>
              <div class="text-center">No data available</div>
            </template>
            <template v-slot:[`item.program_id`]="{ item }">
              {{ getProgramName(item.program_id) }}
            </template>
            <template v-slot:[`item.partner_id`]="{ item }">
              {{ getPartnerName(item.partner_id) }}
            </template>
            <template v-slot:[`item.dob`]="{ item }">
              {{ item.dob ? formatDate(item.dob) : "-" }}
            </template>
            <template v-slot:[`item.batch`]="{ item }">
              {{ item.year }}-{{ item.batch }}
            </template>
          </v-data-table>
        </v-sheet>
      </v-col>
    </v-row>

    <!-- Pending History Dialog -->
    <v-dialog v-model="pendingHistoryDialog" width="800" persistent scrollable>
      <pendingHistory
        v-if="pendingHistoryDialog"
        @closeDialog="pendingHistoryDialog = false"
        :pendings="pendings"
        :applicantId="currentApplicantId"
      />
    </v-dialog>

    <!-- Rejected Reason Dialog -->
    <v-dialog v-model="rejectDialog" max-width="600" persistent scrollable>
      <v-card class="secondary">
        <v-card-title class="primary white--text">
          {{ isVerifier ? "Verifier" : "Rejected" }} Reason -
          {{ currentApplicantId }}
          <v-spacer></v-spacer>
          <v-btn small icon @click="rejectDialog = false">
            <v-icon small color="secondary">mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="mt-3 reset-card-text">
          <div v-if="rejectedReason" v-html="rejectedReason"></div>
          <div v-else>No Rejected Reason</div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import pendingHistory from "@/components/applicant/pendingHistory.vue";
import { rules } from "@/helpers/rules";
import { reportHeaders, preApplicantHeaders } from "@/constants/headers";

const LIST_STATE_KEY = "report_list_state";
const EDIT_STATE_KEY = "report_edit_state";

export default {
  name: "ReportListView",
  components: { pendingHistory },

  data() {
    return {
      loading: false,

      // Set from sessionStorage in created()
      headerKey: "",
      headerValue: "",
      preApplicant: false, // always a real boolean here

      form: {
        year_batch: null,
        application_status: null,
        program_id: null,
        partner_id: null,
        reviewed_user_id: null,
        application_id: null,
        employee_id: null,
        mobile_no: null,
      },

      reportData: [],
      preApplicantData: [],
      programs: [],
      partners: [],
      users: [],

      headers: reportHeaders,
      dynamicHeaders: [],
      preApplicantHeaders,

      pendingHistoryDialog: false,
      pendings: [],
      rejectDialog: false,
      rejectedReason: null,
      verifierRejectDialog: false,
      verifierRejectedReason: null,
      currentApplicantId: "",

      rules,
      statuses: [
        "Submitted",
        "Approved",
        "Rejected",
        "Admitted",
        "Provisionally Approved",
        "Pending",
        "Re-Submitted",
      ],
      isVerifier: false
    };
  },

  computed: {
    currentUser() {
      return this.$store.state.currentUser;
    },
    verifierList() {
      return this.users.filter((item) => item.role === "Verifier");
    },
    reviewerList() {
      return this.users.filter(
        (item) => item.role === "Reviewer" || item.id === 1,
      );
    },
  },

  async created() {
    // Read state from sessionStorage — always correct types (no URL serialization)
    const raw = sessionStorage.getItem(LIST_STATE_KEY);
    if (raw) {
      try {
        const saved = JSON.parse(raw);
        this.headerKey = saved.headerKey || "";
        this.headerValue = saved.headerValue || "";
        this.preApplicant = Boolean(saved.preApplicant); // always boolean
        this.viewType = saved.viewType || "overall";
        this.fromDate = saved.fromDate || null;
        this.toDate = saved.toDate || null;
        if (saved.filters) {
          Object.assign(this.form, saved.filters);
        }
      } catch (e) {
        console.warn("Failed to parse list state from sessionStorage", e);
      }
    }
    let subTitle = this.headerValue || this.headerKey;
    if (this.viewType === "daywise" && this.fromDate && this.toDate) {
      const fmt = (d) =>
        new Date(d).toLocaleDateString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        });
      if (this.fromDate === this.toDate) {
        subTitle += ` · ${fmt(this.fromDate)}`;
      } else {
        subTitle += ` · ${fmt(this.fromDate)} – ${fmt(this.toDate)}`;
      }
    }

    this.$store.commit("setBreadcrumbs", [
      { text: "Operational Dashboard", to: { name: "Operational Dashboard" } },
      { text: subTitle },
    ]);

    await Promise.all([
      this.getPrograms(),
      this.getPartners(),
      this.getUsers(),
    ]);
    await this.fetchData();
  },

  methods: {
    async fetchData() {
      if (this.preApplicant) {
        await this.fetchPreApplicants();
      } else {
        await this.fetchReport();
      }
    },

    search() {
      this.fetchData();
    },

    async fetchReport() {
      this.loading = true;
      const params = { ...this.form };

      // if (this.viewType === 'daywise' && this.fromDate && this.toDate) {
      //   params.date_from = this.fromDate;
      //   params.date_to = this.toDate;
      // }
      const qs = this.objectToQueryParams(params);
      await this.$axios
        .get(`/applicant_details?${qs}`)
        .then((res) => {
          this.reportData = res.data.data;

          let h = [...this.headers];
          if (this.form.application_status !== "Admitted")
            h = h.filter((x) => x.value !== "usn_id");
          if (this.headerKey !== "Partner")
            h = h.filter((x) => x.value !== "partner_approval_status");
          if (this.form.application_status !== "Rejected") h = h.slice(0, -1);
          this.dynamicHeaders = h.filter(
            (x) =>
              !this.form[x.value] ||
              this.form[x.value] === "" ||
              this.form[x.value] === null,
          );
        })
        .catch(async (err) => {
          const msg = await this.$store.dispatch("throwError", err.response);
          this.$store.commit("showSnackbar", { message: msg, color: "error" });
        })
        .finally(() => {
          this.loading = false;
        });
    },

    async fetchPreApplicants() {
      this.loading = true;
      try {
        const params = { ...this.form };
        // if (this.viewType === 'daywise' && this.fromDate && this.toDate) {
        //   params.date_from = this.fromDate;
        //   params.date_to = this.toDate;
        // }

        const qs = this.objectToQueryParams(params);
        const res = await this.$axios.get(
          `/pre_applications?${qs}&!application_id=true`,
        );
        this.preApplicantData = res.data.data;
      } catch (e) {
        console.log(e);
      } finally {
        this.loading = false;
      }
    },

    goToEdit(item) {
      // Write edit state to sessionStorage so edit.vue gets real types
      sessionStorage.setItem(
        EDIT_STATE_KEY,
        JSON.stringify({
          id: item.id,
          programId: item.program_id,
          applicationId: item.application_id,
          headerKey: this.headerKey,
          headerValue: this.headerValue,
          fromDashboard: false,
        }),
      );
      this.$router.push({ name: "Operational Dashboard Edit" });
    },

    async getPendingHistory(item) {
      await this.$axios
        .get(`/pending_reasons?applicant_detail_id=${item.id}`)
        .then((res) => {
          this.pendings = res.data.data;
          if (this.pendings.length > 0) {
            this.currentApplicantId = item.application_id;
            this.$set(item, "pendingHistory", false);
            this.pendingHistoryDialog = true;
          } else {
            this.$set(item, "pendingHistory", true);
            this.pendingHistoryDialog = false;
            this.currentApplicantId = null;
          }
        })
        .catch(async (err) => {
          const msg = await this.$store.dispatch("throwError", err.response);
          this.$store.commit("showSnackbar", { message: msg, color: "error" });
        });
    },

    async getPrograms() {
      try {
        if (!this.form.year_batch) return;
        const [year, batch] = this.form.year_batch.split("-");
        const res = await this.$axios.get(
          `/get_batch_programs/${year}/${batch}`,
        );
        this.programs = res.data.data;
      } catch (e) {
        console.log(e);
      }
    },

    async getPartners() {
      try {
        const res = await this.$axios.get("/partners");
        this.partners = res.data.data;
      } catch (e) {
        console.log(e);
      }
    },

    async getUsers() {
      try {
        const res = await this.$axios.get("/users");
        this.users = res.data.data;
      } catch (e) {
        console.log(e);
      }
    },

    getProgramName(id) {
      const p = this.programs.find((p) => p.id === id);
      return p
        ? p.degree.short_name +
            (p.specialization ? ` - ${p.specialization}` : "")
        : "";
    },

    getPartnerName(id) {
      const p = this.partners.find((p) => p.id === id);
      return p ? p.name : "";
    },

    getUserName(userId) {
      const u = this.users.find((u) => u.id === userId);
      return u ? `${u.first_name} ${u.last_name}` : "";
    },

    formatDate(date) {
      const d = new Date(date);
      return `${String(d.getDate()).padStart(2, "0")}-${String(
        d.getMonth() + 1,
      ).padStart(2, "0")}-${d.getFullYear()}`;
    },

    objectToQueryParams(form) {
      let obj = { ...form };
      if (obj.application_status === "Provisional")
        obj.application_status = "Provisionally Approved";
      if (obj.year_batch) {
        const [year, batch] = obj.year_batch.split("-");
        obj.year = year;
        obj.batch = batch;
        delete obj.year_batch;
      }
      obj = this.getDateRangeParams(obj);
      return Object.entries(obj)
        .filter(([_, v]) => v !== undefined && v !== null && v !== "")
        .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
        .join("&");
    },
    getDateRangeParams(obj) {
      if (this.viewType !== "daywise" || !this.fromDate || !this.toDate)
        return obj;
      if (this.headerValue == "Admitted") {
        obj.admitted_date_from = this.fromDate;
        obj.admitted_date_to = this.toDate;
      } else if (this.headerValue == "Approved") {
        obj.approved_at_from = this.fromDate;
        obj.approved_at_to = this.toDate;
      } else if (this.headerValue == "Provisional") {
        obj.provisionally_approved_at_from = this.fromDate;
        obj.provisionally_approved_at_to = this.toDate;
      } else if (this.headerValue == "Submitted") {
        obj.submitted_date_from = this.fromDate;
        obj.submitted_date_to = this.toDate;
      } else if (this.headerValue == "Draft") {
        obj.inserted_at_from = this.fromDate;
        obj.inserted_at_to = this.toDate;
      } else {
        obj.updated_at_from = this.fromDate;
        obj.updated_at_to = this.toDate;
      }
      return obj;
    },
  },
};
</script>

<style scoped>
.pre-applicant::v-deep td {
  font-size: 13px !important;
  padding-right: 0px !important;
}
</style>
